/*
 * Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {animate, AnimationEvent, state, style, transition, trigger} from '@angular/animations';
import {
    Component,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChange,
    ViewChild
} from '@angular/core';

import {FocusTrapDirective} from '../utils/focus-trap/focus-trap.directive';
import {ScrollingService} from '../utils/scrolling/scrolling-service';

import {GHOST_PAGE_ANIMATION} from './utils/ghost-page-animations';
export class Modal implements OnChanges, OnDestroy {
     focusTrap: FocusTrapDirective;
      _open: boolean = false;
     _openChanged: EventEmitter<boolean> = new EventEmitter<boolean>(false);

     closable: boolean = true;
     size: string;
     staticBackdrop: boolean = false;
     skipAnimation: string = "false";

    // presently this is only used by wizards
     ghostPageState: string = "hidden";
     bypassScrollService: boolean = false;
     stopClose: boolean = false;
     altClose: EventEmitter<boolean> = new EventEmitter<boolean>(false);
/**
 * @param {?} _scrollingService
 */
constructor(private _scrollingService: ScrollingService) {}
/**
 * @return {?}
 */
get sizeClass(): string {
        if (this.size) {
            return "modal-" + this.size;
        } else {
            return "";
        }
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: {[propName: string]: SimpleChange}): void {
        if (!this.bypassScrollService && changes && changes.hasOwnProperty("_open")) {
            if (changes._open.currentValue) {
                this._scrollingService.stopScrolling();
            } else {
                this._scrollingService.resumeScrolling();
            }
        }
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        this._scrollingService.resumeScrolling();
    }
/**
 * @return {?}
 */
open(): void {
        if (this._open === true) {
            return;
        }
        this._open = true;
        this._openChanged.emit(true);
    }
/**
 * @return {?}
 */
close(): void {
        if (this.stopClose) {
            this.altClose.emit(false);
            return;
        }
        if (!this.closable || this._open === false) {
            return;
        }
        this._open = false;
        // todo: remove this after animation bug is fixed https://github.com/angular/angular/issues/15798
        // this was handled by the fadeDone event below, but that AnimationEvent is not firing in Angular 4.0.
        this._openChanged.emit(false);
        // SPECME
        this.focusTrap.setPreviousFocus();  // Handles moving focus back to the element that had it before.
    }
/**
 * @param {?} e
 * @return {?}
 */
fadeDone(e: AnimationEvent) {
        if (e.toState === "void") {
            this._openChanged.emit(false);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-modal",
    viewProviders: [ScrollingService],
    template: `

      <!--
        ~ Copyright (c) 2016-2017 VMware, Inc. All Rights Reserved.
        ~ This software is released under MIT license.
        ~ The full license information can be found in LICENSE in the root directory of this project.
        -->

      <div clrFocusTrap class="modal" *ngIf="_open">
          <!--fixme: revisit when ngClass works with exit animation-->
          <div [@fadeDown]="skipAnimation" (@fadeDown.done)="fadeDone($event)"
               class="modal-dialog"
               [class.modal-sm]="size == 'sm'"
               [class.modal-lg]="size == 'lg'"
               [class.modal-xl]="size == 'xl'"
               role="dialog" aria-hidden="true">

              <div class="modal-outer-wrapper">
                  <div class="modal-content-wrapper">
                      <!-- only used in wizards -->
                      <ng-content select=".modal-nav"></ng-content>

                      <div class="modal-content">
                          <div class="modal-header">
                              <button type="button" class="close" aria-label="Close"
                                      *ngIf="closable" (click)="close()">
                                  <clr-icon aria-hidden="true" shape="close"></clr-icon>
                              </button>
                              <ng-content select=".modal-title"></ng-content>
                          </div>
                          <ng-content select=".modal-body"></ng-content>
                          <ng-content select=".modal-footer"></ng-content>
                      </div>
                  </div>
                  <div class="modal-ghost-wrapper">
                      <div [@ghostPageOneState]="ghostPageState" class="modal-ghost modal-ghost-1"></div>
                      <div [@ghostPageTwoState]="ghostPageState" class="modal-ghost modal-ghost-2"></div>
                  </div>
              </div>
          </div>

          <div [@fade] class="modal-backdrop"
               aria-hidden="true"
               (click)="staticBackdrop || close()"></div>
      </div>
    `,
    styles: [`
        :host { display: none; }
        :host.open { display: inline; }
    `],
    animations: [
        trigger("fadeDown",
                [
                    transition("* => false",
                               [style({opacity: 0, transform: "translate(0, -25%)"}), animate("0.2s ease-in-out")]),
                    transition("false => *",
                               [animate("0.2s ease-in-out", style({opacity: 0, transform: "translate(0, -25%)"}))])
                ]),
        trigger("fade",
                [
                    transition("void => *", [style({opacity: 0}), animate("0.2s ease-in-out", style({opacity: 0.85}))]),
                    transition("* => void", [animate("0.2s ease-in-out", style({opacity: 0}))])
                ]),
        trigger("ghostPageOneState",
                [
                    state(GHOST_PAGE_ANIMATION.STATES.NO_PAGES, style({left: "-24px"})),
                    state(GHOST_PAGE_ANIMATION.STATES.ALL_PAGES, style({left: "0"})),
                    state(GHOST_PAGE_ANIMATION.STATES.NEXT_TO_LAST_PAGE, style({left: "-24px"})),
                    state(GHOST_PAGE_ANIMATION.STATES.LAST_PAGE, style({left: "-24px"})),
                    transition(GHOST_PAGE_ANIMATION.STATES.NO_PAGES + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.IN)),
                    transition(GHOST_PAGE_ANIMATION.STATES.ALL_PAGES + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.OUT)),
                    transition(GHOST_PAGE_ANIMATION.STATES.LAST_PAGE + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.IN)),
                    transition(GHOST_PAGE_ANIMATION.STATES.NEXT_TO_LAST_PAGE + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.OUT))
                ]),
        // TODO: USE TRANSFORM, NOT LEFT...
        trigger("ghostPageTwoState",
                [
                    state(GHOST_PAGE_ANIMATION.STATES.NO_PAGES, style({left: "-24px", top: "24px", bottom: "24px"})),
                    state(GHOST_PAGE_ANIMATION.STATES.ALL_PAGES, style({left: "24px"})),
                    state(GHOST_PAGE_ANIMATION.STATES.NEXT_TO_LAST_PAGE,
                          style({left: "0px", top: "24px", bottom: "24px", background: "#bbb"})),
                    state(GHOST_PAGE_ANIMATION.STATES.LAST_PAGE, style({left: "-24px", top: "24px", bottom: "24px"})),
                    transition(GHOST_PAGE_ANIMATION.STATES.NO_PAGES + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.IN)),
                    transition(GHOST_PAGE_ANIMATION.STATES.ALL_PAGES + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.OUT)),
                    transition(GHOST_PAGE_ANIMATION.STATES.LAST_PAGE + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.IN)),
                    transition(GHOST_PAGE_ANIMATION.STATES.NEXT_TO_LAST_PAGE + " => *",
                               animate(GHOST_PAGE_ANIMATION.TRANSITIONS.OUT))
                ])
    ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ScrollingService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'focusTrap': [{ type: ViewChild, args: [FocusTrapDirective, ] },],
'_open': [{ type: HostBinding, args: ["class.open", ] },{ type: Input, args: ["clrModalOpen", ] },],
'_openChanged': [{ type: Output, args: ["clrModalOpenChange", ] },],
'closable': [{ type: Input, args: ["clrModalClosable", ] },],
'size': [{ type: Input, args: ["clrModalSize", ] },],
'staticBackdrop': [{ type: Input, args: ["clrModalStaticBackdrop", ] },],
'skipAnimation': [{ type: Input, args: ["clrModalSkipAnimation", ] },],
'ghostPageState': [{ type: Input, args: ["clrModalGhostPageState", ] },],
'bypassScrollService': [{ type: Input, args: ["clrModalOverrideScrollService", ] },],
'stopClose': [{ type: Input, args: ["clrModalPreventClose", ] },],
'altClose': [{ type: Output, args: ["clrModalAlternateClose", ] },],
'close': [{ type: HostListener, args: ["body:keyup.escape", ] },],
};
}

function Modal_tsickle_Closure_declarations() {
/** @type {?} */
Modal.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Modal.ctorParameters;
/** @type {?} */
Modal.propDecorators;
/** @type {?} */
Modal.prototype.focusTrap;
/** @type {?} */
Modal.prototype._open;
/** @type {?} */
Modal.prototype._openChanged;
/** @type {?} */
Modal.prototype.closable;
/** @type {?} */
Modal.prototype.size;
/** @type {?} */
Modal.prototype.staticBackdrop;
/** @type {?} */
Modal.prototype.skipAnimation;
/** @type {?} */
Modal.prototype.ghostPageState;
/** @type {?} */
Modal.prototype.bypassScrollService;
/** @type {?} */
Modal.prototype.stopClose;
/** @type {?} */
Modal.prototype.altClose;
/** @type {?} */
Modal.prototype._scrollingService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
